import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "shared/contexts/I18nContext";
import { useLoading } from "shared/contexts/Loading.jsx";
import { useSubscription } from "shared/contexts/Subscription";
import apiCall from "shared/services/ApiAdapter";
import { useStudents } from "shared/contexts/Student";
import { useMasterList } from "shared/hooks/useMasterList";
import ContentHeaderSection from "../../../shared/common/ContentHeaderSection";
import {
  getCurrentUserId,
  getCurrentUserRole,
  getUserRoleProfileId,
} from "shared/utils/SessionDataUtils";

/**
 * Form for creating or updating an enrollment request for a student.
 * This form is simplified and does not require a course or batch selection.
 * @param {object} props - The component props.
 * @param {function} props.navigateTo - Function to navigate to a different view.
 * @param {string} props.studentId - The ID of the student the request is for.
 * @param {string} props.enrollmentId - The ID of the enrollment to update (optional).
 * @param {string} props.courseId - The ID of the course requested by the student (optional).
 * @param {function} props.displayToast - Function to display toast notifications.
 */
const CreateUpdateRequestEnrollmentForm = ({
  navigateTo,
  studentId,
  courseId,
  enrollmentId,
  displayToast,
}) => {
  const { t } = useTranslation();
  const { startLoading, stopLoading } = useLoading();
  const { limits, canPerformAction } = useSubscription();
  const userId = getCurrentUserId();
  const storedParentId = getUserRoleProfileId();
  const role = getCurrentUserRole();

  const isUpdateMode = !!enrollmentId;

  // Initial state for the enrollment request form
  const [enrollmentRequest, setEnrollmentRequest] = useState({
    studentId: studentId,
    courseId: courseId,
    enrollmentDate: new Date().toISOString().split("T")[0],
    status: "Requested", // Default status is "Requested"
  });
  const [studentDetails, setStudentDetails] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  // State to hold validation errors
  const [validationErrors, setValidationErrors] = useState({});

  // Use the master list hook to fetch curricula and academic classes
  const {
    masterData: { curricula, academicClasses },
    isLoading: masterDataLoading,
    error: masterDataError,
  } = useMasterList({ displayToast, startLoading, stopLoading });

  // Helper function to get the label from the master list data
  const getLabel = useCallback((value, list) => {
    if (!list || !Array.isArray(list)) return value || "";
    const byValue = list.find((i) => String(i.value) === String(value));
    if (byValue) return byValue.label;
    const idNum = Number(value);
    if (!Number.isNaN(idNum)) {
      const byId = list.find((i) => i.id === idNum);
      if (byId) return byId.label;
    }
    return value;
  }, []);

  // Effect to fetch initial data: student details and existing enrollment request
  useEffect(() => {
    const fetchData = async () => {
      setFormLoading(true);
      try {
        let initialRequest = null;
        let fetchedStudentData = null;

        if (isUpdateMode) {
          // Fetch existing enrollment request data in update mode
          initialRequest = await apiCall(
            `/batch-enrollments/${enrollmentId}`,
            false,
            "GET",
            null,
            startLoading,
            stopLoading,
            displayToast
          );
          setEnrollmentRequest({
            ...initialRequest,
            enrollmentDate: initialRequest.enrollmentDate
              ? initialRequest.enrollmentDate.split("T")[0]
              : new Date().toISOString().split("T")[0],
            studentId: initialRequest.student?.id,
            courseId: initialRequest.course?.id,
          });
          fetchedStudentData = initialRequest.student;
          // If nested student is missing or incomplete, fetch by id
          if (
            !fetchedStudentData ||
            !fetchedStudentData.name ||
            !fetchedStudentData.email
          ) {
            const sid =
              initialRequest?.student?.id || initialRequest?.studentId;
            if (sid) {
              try {
                fetchedStudentData = await apiCall(
                  `/students/${sid}`,
                  false,
                  "GET",
                  null,
                  startLoading,
                  stopLoading,
                  displayToast
                );
              } catch (e) {
                console.warn(
                  "Failed to fetch student by id for enrollment request:",
                  e
                );
              }
            }
          }
        } else {
          // Fetch student details in create mode
          if (studentId) {
            fetchedStudentData = await apiCall(
              `/students/${studentId}`,
              false,
              "GET",
              null,
              startLoading,
              stopLoading,
              displayToast
            );
          }
        }
        setStudentDetails(fetchedStudentData);
      } catch (error) {
        console.error(
          "Failed to fetch data for enrollment request form:",
          error
        );
        displayToast(`Failed to load data: ${error.message}`, "error");
      } finally {
        setFormLoading(false);
      }
    };
    fetchData();
  }, [
    studentId,
    enrollmentId,
    isUpdateMode,
    startLoading,
    stopLoading,
    displayToast,
  ]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    // Clear the error for this field as the user interacts with it
    setValidationErrors((prev) => ({ ...prev, [name]: "" }));
    setEnrollmentRequest((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    // Perform inline validation
    const errors = {};
    if (!enrollmentRequest.enrollmentDate) {
      errors.enrollmentDate = "Please select a date.";
    }
    if (!enrollmentRequest.status) {
      errors.status = "Please select a status.";
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setFormLoading(false);
      return;
    }

    if (!isUpdateMode && !canPerformAction("maxStudentsPerTutor", 0)) {
      displayToast(
        `Cannot create enrollment request. ${t("limitReached")}`,
        "error"
      );
      setFormLoading(false);
      return;
    }

    // Conditionally build the enrollment request object for the API call
    const requestToSave = {
      ...enrollmentRequest,
      tutorId: userId, // Add the current user as the tutor
    };

    // Add or update audit fields based on mode
    if (isUpdateMode) {
      requestToSave.updatedBy = userId;
      requestToSave.updatedDate = new Date().toISOString();
    } else {
      requestToSave.createdBy = userId;
    }

    // Add related entities only if their IDs exist
    if (enrollmentRequest.studentId) {
      requestToSave.student = { id: enrollmentRequest.studentId };
    }
    if (enrollmentRequest.courseId) {
      requestToSave.course = { id: enrollmentRequest.courseId };
    }

    // Remove parent object for updates to avoid foreign key errors
    if (isUpdateMode) {
      delete requestToSave.parent;
    } else if (storedParentId && role === "ROLE_PARENT") {
      // Only include the parent ID on creation
      requestToSave.parent = { id: storedParentId };
    }

    try {
      if (isUpdateMode) {
        // UPDATE request
        await apiCall(
          `/batch-enrollments/${enrollmentId}`,
          false,
          "PUT",
          requestToSave,
          startLoading,
          stopLoading,
          displayToast
        );
        displayToast(t("enrollmentRequestSuccessfullyUpdated"), "success");
      } else {
        // CREATE request
        await apiCall(
          "/batch-enrollments",
          false,
          "POST",
          requestToSave,
          startLoading,
          stopLoading,
          displayToast
        );
        displayToast(t("enrollmentRequestSuccessfullyCreated"), "success");
      }

      // Navigate back to the students list after successful creation
      navigateTo("students");
    } catch (error) {
      console.error("Failed to save enrollment request:", error.message);
      if (error?.message?.includes("violates unique constraint")) {
        displayToast(
          "An enrollment request already exists for this student.",
          "error"
        );
      } else {
        displayToast(t("failedToSaveEnrollmentRequest"), "error");
      }
    } finally {
      setFormLoading(false);
    }
  };

  const isLoading = formLoading || masterDataLoading;

  if (masterDataError) {
    return (
      <div className="p-8 md:px-10 text-center text-red-500">
        {masterDataError}
      </div>
    );
  }

  if (isLoading && !studentDetails) {
    return (
      <div className="p-8 md:px-10 text-center text-gray-600 dark:text-gray-400">
        {t("loadingEnrollmentDetails")}
      </div>
    );
  }

  const formTitle = isUpdateMode
    ? t("updateEnrollmentRequest")
    : t("createEnrollmentRequest");
  const submitButtonText = isUpdateMode ? t("update") : t("create");

  return (
    <div className="p-8 md:px-10">
      <ContentHeaderSection
        title={
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigateTo("students")}
              className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none"
              aria-label="Go back to students"
            >
              <svg
                className="w-6 h-6 text-gray-600 dark:text-gray-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                ></path>
              </svg>
            </button>
            <span className="text-2xl font-semibold text-gray-900 dark:text-gray-100 tracking-tight leading-tight">
              {formTitle}
            </span>
          </div>
        }
        subtitle={t("enrollmentRequestDetails")}
      />
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 max-w-2xl mx-auto">
        <form onSubmit={handleSubmit} className="space-y-4">
          {studentDetails && (
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-md">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                {t("student")}: {studentDetails.name}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Email: {studentDetails.email}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Class: {getLabel(studentDetails.academicClass, academicClasses)}{" "}
                | Curriculum: {getLabel(studentDetails.curriculum, curricula)}
              </p>
            </div>
          )}

          <div>
            <label
              htmlFor="enrollmentDate"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {t("enrollmentDate")}
            </label>
            <input
              type="date"
              id="enrollmentDate"
              name="enrollmentDate"
              value={enrollmentRequest.enrollmentDate}
              onChange={handleChange}
              className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-gray-100 ${
                validationErrors.enrollmentDate
                  ? "border-red-500 dark:border-red-500"
                  : "border-gray-300 dark:border-gray-600"
              }`}
            />
            {validationErrors.enrollmentDate && (
              <p className="text-sm text-red-500 mt-1">
                {validationErrors.enrollmentDate}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="status"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {t("status")}
            </label>
            <select
              id="status"
              name="status"
              value={enrollmentRequest.status}
              onChange={handleChange}
              className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-gray-100 ${
                validationErrors.status
                  ? "border-red-500 dark:border-red-500"
                  : "border-gray-300 dark:border-gray-600"
              }`}
              disabled={!isUpdateMode}
            >
              <option value="Requested">Requested</option>
              <option value="Cancelled">Cancelled</option>
              {isUpdateMode && (
                <>
                  <option value="Enrolled">Enrolled</option>
                  <option value="Rejected">Rejected</option>
                </>
              )}
            </select>
            {validationErrors.status && (
              <p className="text-sm text-red-500 mt-1">
                {validationErrors.status}
              </p>
            )}
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="submit"
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              disabled={isLoading}
            >
              {isLoading
                ? isUpdateMode
                  ? t("updating")
                  : t("creating")
                : submitButtonText}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateUpdateRequestEnrollmentForm;
